/**
 * Provider-agnostic creative detection for display slots.
 *
 * A slot is "filled" once a creative element appears inside it: an iframe /
 * img / video / canvas / object / embed whose declared (attribute or inline
 * style) size, or loaded image size, exceeds 1×1. Tracking pixels (1×1) and
 * sync frames (0×0) never count.
 *
 * No-fill: adapters mark their unit with data-tpg-ad-state — "requested" when
 * the bid is issued, "activation-failed" when the unit could never be
 * activated. The no-fill timer starts at "requested" (never at mount), so a
 * slow loader or late layout cannot collapse a slot that is still bidding.
 * Slots whose adapter never marks a state are never timed out.
 *
 * Each watcher reports at most once (creative OR no-fill) and is fully torn
 * down by the returned stop function.
 */

export var DISPLAY_NO_FILL_TIMEOUT_MS = 10000;

var CREATIVE_TAGS = {
  IFRAME: true,
  IMG: true,
  VIDEO: true,
  CANVAS: true,
  OBJECT: true,
  EMBED: true,
};

function declaredSize(el, name) {
  var attr = el.getAttribute(name);
  var value = attr != null && attr !== "" ? parseFloat(attr) : NaN;
  if (!(value >= 0) && el.style && el.style[name]) {
    value = parseFloat(el.style[name]);
  }
  return value;
}

function isCreativeElement(el) {
  if (!el || !CREATIVE_TAGS[el.tagName]) return false;
  var width = declaredSize(el, "width");
  var height = declaredSize(el, "height");
  if (width >= 0 && height >= 0) return width > 1 && height > 1;
  if (el.tagName === "IMG") {
    return !!el.complete && el.naturalWidth > 1 && el.naturalHeight > 1;
  }
  if (el.tagName === "IFRAME") return false;
  if (typeof el.getBoundingClientRect !== "function") return false;
  var rect = el.getBoundingClientRect();
  return rect.width > 1 && rect.height > 1;
}

/**
 * @param {HTMLElement} container
 * @returns {HTMLElement|null}
 */
export function findDisplayCreative(container) {
  if (!container || typeof container.querySelectorAll !== "function") return null;
  var nodes = container.querySelectorAll("iframe, img, video, canvas, object, embed");
  for (var i = 0; i < nodes.length; i += 1) {
    if (isCreativeElement(nodes[i])) return nodes[i];
  }
  return null;
}

/**
 * Every creative element in the slot, in DOM order.
 *
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
export function findDisplayCreatives(container) {
  var out = [];
  if (!container || typeof container.querySelectorAll !== "function") return out;
  var nodes = container.querySelectorAll("iframe, img, video, canvas, object, embed");
  for (var i = 0; i < nodes.length; i += 1) {
    if (isCreativeElement(nodes[i])) out.push(nodes[i]);
  }
  return out;
}

function requestState(container) {
  if (!container || typeof container.querySelector !== "function") return "";
  var marked = container.querySelector("[data-tpg-ad-state]");
  return marked ? marked.getAttribute("data-tpg-ad-state") || "" : "";
}

/**
 * @param {HTMLElement} container
 * @param {{onCreative?: function(HTMLElement): void, onNoFill?: function(string): void}} handlers
 * @param {{timeoutMs?: number}} [options]
 * @returns {function(): void} stop
 */
export function watchDisplayCreative(container, handlers, options) {
  var opts = options || {};
  var timeoutMs =
    typeof opts.timeoutMs === "number" && opts.timeoutMs > 0
      ? opts.timeoutMs
      : DISPLAY_NO_FILL_TIMEOUT_MS;
  var done = false;
  var timer = null;
  var observer = null;
  var pendingImages = [];

  function stop() {
    done = true;
    if (timer != null) {
      window.clearTimeout(timer);
      timer = null;
    }
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    pendingImages.forEach(function (img) {
      img.removeEventListener("load", check);
    });
    pendingImages = [];
  }

  function report(kind, value) {
    if (done) return;
    stop();
    var fn = handlers && handlers[kind];
    if (typeof fn === "function") fn(value);
  }

  function listenForImages() {
    var images = container.querySelectorAll("img");
    for (var i = 0; i < images.length; i += 1) {
      if (!images[i].complete && pendingImages.indexOf(images[i]) < 0) {
        images[i].addEventListener("load", check);
        pendingImages.push(images[i]);
      }
    }
  }

  function check() {
    if (done) return;
    var creative = findDisplayCreative(container);
    if (creative) {
      report("onCreative", creative);
      return;
    }
    var state = requestState(container);
    if (state === "activation-failed") {
      report("onNoFill", "activation_failed");
      return;
    }
    if (state === "requested" && timer == null) {
      timer = window.setTimeout(function () {
        timer = null;
        if (done) return;
        var late = findDisplayCreative(container);
        if (late) report("onCreative", late);
        else report("onNoFill", "timeout");
      }, timeoutMs);
    }
    listenForImages();
  }

  if (!container || typeof window === "undefined") return stop;
  if (typeof window.MutationObserver === "function") {
    observer = new window.MutationObserver(check);
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-tpg-ad-state", "width", "height", "style", "src"],
    });
  }
  check();
  return stop;
}
